import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { Button, Divider, Notification, Paper, Text, TextInput, Group, Modal } from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import { functions, db } from "../firebase";
import { useForm } from "@mantine/form";
import { getDocs, collection, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { fetchImageDataUrl } from "../components/shared/hooks/data-functions";
import { PageHeader } from "../components/shared";

export interface AdvancedSettingsProps {
  companyId: string;
}

export interface CreditTransferFormValues {
  receivingCompanyId: string;
  creditAmount: string;
  reference: string;
}

export function AdvancedSettings({ companyId }: AdvancedSettingsProps) {
  const [message, setMessage] = useState<string>("");
  const [messageColor, setMessageColor] = useState<string>("red");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [loading, setLoading] = useState<boolean>(false);

  const form = useForm<CreditTransferFormValues>({
    initialValues: {
      receivingCompanyId: "",
      creditAmount: "",
      reference: "",
    },
    validate: (values) => ({
      receivingCompanyId: !values.receivingCompanyId ? "Receiving Company ID is required" : undefined,
      creditAmount: !values.creditAmount || Number(values.creditAmount) < 1 ? "Credit Amount is required" : undefined,
      reference: !values.reference ? "Reference is required" : undefined,
    }),
  });

  const handleTransferCredits = async (values: CreditTransferFormValues) => {
    const transferCredits = httpsCallable(functions, "transferCredits");

    try {
      const result = await transferCredits({
        fromCompanyId: companyId, 
        toCompanyId: values.receivingCompanyId,
        creditQuantity: parseInt(values.creditAmount, 10),
        reference: values.reference,
      });
      setMessageColor("green");
      setMessage((result.data as any)?.result || "Credits transferred successfully");
    } catch (error: any) {
      setMessageColor("red");
      setMessage(`Error: ${error.message}`);
    }
  };

  const handleDownloadSlips = async () => {
    const startDate = dateRange[0];
    const endDate = dateRange[1];
    if (!startDate || !endDate) return;

    setLoading(true);
    setMessage("");
    try {
      const start = Timestamp.fromDate(startDate);
      const end = Timestamp.fromDate(new Date(endDate.getTime() + 24 * 60 * 60 * 1000 - 1));
      const q = query(
        collection(db, 'capturedRefuel'),
        where('companyId', '==', companyId),
        where('transactionDate', '>=', start),
        where('transactionDate', '<=', end),
        orderBy('transactionDate'),
        limit(1000)
      );
      const querySnapshot = await getDocs(q);
      const zip = new JSZip();
      let imageCount = 0;
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();
        if (data.slipPhoto && Array.isArray(data.slipPhoto)) {
          for (let i = 0; i < data.slipPhoto.length; i++) {
            const photo = data.slipPhoto[i];
            if (photo.path) {
              const imageData = await fetchImageDataUrl(photo.path);
              if (imageData) {
                const secs = data.transactionDate?.seconds || 0;
                const dateStr = new Date(secs * 1000).toISOString().split('T')[0];
                const fileName = `${dateStr}_${i}_SlipPhoto.jpg`;
                const base64Data = imageData.split(',')[1];
                zip.file(fileName, base64Data, { base64: true });
                imageCount++;
              } else {
                console.error("Failed to fetch image:", photo.path);
              }
            }
          }
        }
      }
      if (imageCount === 0) {
        setMessageColor("red");
        setMessage("No slip photos found for the selected date range.");
      } else {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];
        const zipName = `refuel-slips-${startStr}_to_${endStr}.zip`;
        saveAs(zipBlob, zipName);
        setMessageColor("green");
        setMessage(`Downloaded ${imageCount} slip photos.`);
        setModalOpen(false);
        setDateRange([null, null]);
      }
    } catch (error: any) {
      console.error("Error in handleDownloadSlips:", error);
      setMessageColor("red");
      setMessage(`Error downloading slips: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Advanced Operations"
        subtitle="Immediate credit transfers and receipt photo archives"
        alert={
          message ? (
            <Notification
              color={messageColor}
              style={{ maxWidth: "600px" }}
              onClose={() => setMessage('')}
            >
              {message}
            </Notification>
          ) : undefined
        }
      />
      <Paper shadow="xs" p="md" style={{ maxWidth: '800px' }}>
        <Text size="lg" weight={600}>
          Transfer Credits
        </Text>
        <Text size="sm" color="dimmed" sx={{ fontStyle: "italic" }} style={{ marginBottom: "1em" }}>
          Immediate credit transfer to another company
        </Text>
        <form onSubmit={form.onSubmit(handleTransferCredits)}>
          <TextInput
            required
            label="Receiving company ID"
            {...form.getInputProps('receivingCompanyId')}
          />
          <TextInput
            name="creditAmount"
            required
            label="Credit amount"
            {...form.getInputProps('creditAmount')}
            type="number"
          />
          <TextInput
            name="reference"
            label="Reference"
            {...form.getInputProps('reference')}
            required
          />
          <Group position="center" mt="md">
            <Button
              color="cap-navy"
              size="md"
              style={{ width: "50%" }}
              type="submit"
            >
              Transfer Credits
            </Button>
          </Group>
        </form>
      </Paper>
      <Paper shadow="xs" p="md" style={{ maxWidth: '800px', marginTop: '1em' }}>
        <Text size="lg" weight={600}>
          Download Refuel Slips
        </Text>
        <Text size="sm" color="dimmed" sx={{ fontStyle: "italic" }} style={{ marginBottom: "1em" }}>
          Download all fuel receipt photos from a selected date range as a ZIP file
        </Text>
        <Group position="center" mt="md">
          <Button
            color="cap-navy"
            size="md"
            onClick={() => setModalOpen(true)}
          >
            Download onroad refuel slips
          </Button>
        </Group>
      </Paper>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Select Date Range for Refuel Slips"
        size="md"
      >
        <DateRangePicker
          label="Select date range"
          placeholder="Pick dates"
          value={dateRange}
          onChange={setDateRange}
          required
          allowSingleDateInRange
        />
        <Group position="center" mt="md">
          <Button
            color="cap-navy"
            size="md"
            onClick={handleDownloadSlips}
            disabled={!dateRange[0] || loading}
            loading={loading}
          >
            Continue
          </Button>
        </Group>
      </Modal>
    </div>
  );
}

export default AdvancedSettings;
